// @file Query client which supports the stale-while-revalidate strategy by returning cached data immediately and then fetching the latest data in the background.
import window from '@@/bits/global'
import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister'
import { persistQueryClient, removeOldestQuery } from '@tanstack/query-persist-client-core'
import type { QueryFunction } from '@tanstack/vue-query'
import { QueryClient } from '@tanstack/vue-query'
import { del, get, set } from 'idb-keyval'

export const getQueryClient = (): QueryClient | null => window?.queryClient ?? null

export async function createQueryClient(): Promise<QueryClient> {
  const dataVersion = document.documentElement.getAttribute('data-version') ?? ''
  const queryClient = new QueryClient({
    defaultOptions: { queries: { cacheTime: 1000 * 60 * 60 * 24 } },
  })
  const persister = createAsyncStoragePersister({
    storage: {
      setItem: set,
      getItem: get as (key: string) => Promise<string | null>,
      removeItem: del,
    },
    key: process.env.NODE_ENV === 'production' ? 'PADLET_OFFLINE_CACHE' : 'DEV_PADLET_OFFLINE_CACHE',
    retry: removeOldestQuery,
  })

  const [, restorePromise] = persistQueryClient({
    queryClient,
    persister,
    buster: dataVersion, // Invalidate the cache on every deployment
  })
  // Wait for the persisted cache to be restored
  await restorePromise

  return queryClient
}

export async function fetchCachedQuery<T>({
  cacheKey,
  queryFn,
  onSuccess,
}: {
  cacheKey: Array<string | number>
  queryFn: Function
  onSuccess: (response: T) => void
}): Promise<void> {
  const queryClient = getQueryClient()
  if (queryClient == null) {
    const response = await queryFn()
    void onSuccess(response)
    return
  }

  const cachedData: T | undefined = queryClient.getQueryData(cacheKey)
  if (cachedData != null) {
    // optimistically handle the cached data
    void onSuccess(cachedData)
  }

  // fetch to revalidate
  // Note: since the default `staleTime` is 0, the query will always fetch
  // @see https://tanstack.com/query/v4/docs/framework/vue/guides/initial-query-data
  const response: T = await queryClient.fetchQuery({
    queryKey: cacheKey,
    queryFn: queryFn as QueryFunction,
  })

  void onSuccess(response)
}
